import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  linkPath,
  type GroupStore,
  type IntakeSummary,
  useCreateIntakeLink,
} from '../../features/groupOnboarding/api';
import {
  Button,
  Callout,
  Checkbox,
  ChoiceCards,
  CopyField,
  Dialog,
  Field,
  Input,
  Listbox,
} from '../../ui/v2';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Start = 'copy' | 'blank';
type Who = 'send' | 'me';

/**
 * Add a location: name it, pick where its answers start (copy a location or
 * blank), and who fills in the intake. Sending makes a link (emailed when an
 * address is given) and shows it to copy; "I'll fill it in now" opens it.
 */
export const AddLocationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  groupId: number;
  stores: GroupStore[];
}> = ({ open, onClose, groupId, stores }) => {
  const navigate = useNavigate();
  const create = useCreateIntakeLink();
  const [name, setName] = useState('');
  const [start, setStart] = useState<Start>(stores.length ? 'copy' : 'blank');
  const [source, setSource] = useState(stores[0] ? String(stores[0].id) : '');
  const [sameEntity, setSameEntity] = useState(false);
  const [who, setWho] = useState<Who>('send');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [made, setMade] = useState<IntakeSummary | null>(null);

  const reset = () => {
    setName('');
    setStart(stores.length ? 'copy' : 'blank');
    setSource(stores[0] ? String(stores[0].id) : '');
    setSameEntity(false);
    setWho('send');
    setEmail('');
    setErrors({});
    setMade(null);
    create.reset();
  };
  const close = () => {
    reset();
    onClose();
  };

  const sourceName =
    stores.find((s) => String(s.id) === source)?.name ?? 'that location';

  const submit = () => {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Name the location.';
    if (who === 'send' && email.trim() && !EMAIL_RE.test(email.trim()))
      next.email = 'Enter an email like name@restaurant.com.';
    setErrors(next);
    if (next.name || next.email) return;
    create.mutate(
      {
        location_name: name.trim(),
        store_group_id: groupId,
        copy_from_store_id: start === 'copy' && source ? Number(source) : null,
        same_legal_entity: start === 'copy' && sameEntity,
        recipient_email: who === 'send' ? email.trim() : '',
      },
      {
        onSuccess: (intake) => {
          if (who === 'me') {
            close();
            navigate(linkPath(intake.url));
          } else {
            setMade(intake);
          }
        },
      },
    );
  };

  if (made) {
    return (
      <Dialog
        open={open}
        onClose={close}
        title={`${made.location_name}’s link is ready`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                const path = linkPath(made.url);
                close();
                navigate(path);
              }}
            >
              Open it myself
            </Button>
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <CopyField value={made.url} aria-label="Intake link" />
          <p className="text-v2-12 text-v2-fg-3">
            {made.recipient_email
              ? `We emailed it to ${made.recipient_email}. `
              : ''}
            Anyone with the link can fill it in, and it saves as they go.
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a location"
      dismissible={!create.isPending}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={close}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            {who === 'me'
              ? 'Create and start'
              : email.trim()
                ? 'Create and send link'
                : 'Create link'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Location name"
          hint="How it shows on Onboarding. The legal details go in the intake."
          error={errors.name}
        >
          {(id, describedBy) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={!!errors.name}
              value={name}
              autoFocus
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((x) => ({ ...x, name: undefined }));
              }}
            />
          )}
        </Field>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-v2-12 font-medium text-v2-fg-2">
            Start from
          </legend>
          <ChoiceCards<Start>
            aria-label="Start from"
            value={start}
            onChange={setStart}
            options={[
              ...(stores.length
                ? [
                    {
                      value: 'copy' as const,
                      title: 'Copy the setup of an existing location',
                      description:
                        'How Arthur handles orders, bookings, transfers, and events.',
                      reveal: (
                        <div className="flex flex-col gap-3">
                          <div className="max-w-[280px]">
                            <Listbox
                              aria-label="Copy from"
                              value={source}
                              onChange={setSource}
                              options={stores.map((s) => ({
                                value: String(s.id),
                                label: s.name,
                                description:
                                  s.status === 'live' ? 'Live' : undefined,
                              }))}
                            />
                          </div>
                          <div>
                            <Checkbox
                              label={`Same legal entity as ${sourceName}`}
                              checked={sameEntity}
                              onChange={(e) => setSameEntity(e.target.checked)}
                            />
                            <p className="ml-6 mt-0.5 text-v2-12 text-v2-fg-3">
                              Also copies the legal name, legal address, tax ID
                              and signer.
                            </p>
                          </div>
                        </div>
                      ),
                    },
                  ]
                : []),
              {
                value: 'blank',
                title: 'Start with a blank form',
              },
            ]}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-v2-12 font-medium text-v2-fg-2">
            Who fills in the intake?
          </legend>
          <ChoiceCards<Who>
            aria-label="Who fills in the intake?"
            value={who}
            onChange={setWho}
            options={[
              {
                value: 'send',
                title: 'Send it to the store manager',
                description:
                  'They get a link to fill in and confirm each step.',
                reveal: (
                  <Field
                    label="Their email"
                    optional
                    error={errors.email}
                    className="max-w-[320px]"
                  >
                    {(id, describedBy) => (
                      <Input
                        id={id}
                        type="email"
                        inputMode="email"
                        aria-describedby={describedBy}
                        invalid={!!errors.email}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email)
                            setErrors((x) => ({ ...x, email: undefined }));
                        }}
                      />
                    )}
                  </Field>
                ),
              },
              {
                value: 'me',
                title: 'I’ll fill it in now',
                description: 'You can still share the link later.',
              },
            ]}
          />
        </fieldset>

        <Callout tone="neutral">
          Ask Arthur confirms pricing before the location is created. Billing
          doesn’t change until then.
        </Callout>
        {create.isError && (
          <Callout tone="danger">{create.error.message}</Callout>
        )}
      </div>
    </Dialog>
  );
};
